import { useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  Sparkles,
  Wand2,
  Box,
  Loader2,
  Save,
  Download,
  RefreshCw,
  Settings,
  ChevronDown,
  Info,
  Zap,
  Layers,
  Palette,
  Copy,
  Eye,
} from 'lucide-react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, Environment, PerspectiveCamera, Center } from '@react-three/drei'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
import * as THREE from 'three'
import { useEffect, useState as useStateThree } from 'react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { cn } from '@/lib/utils'
import { 
  textTo3DService, 
  TEXT_TO_3D_PROVIDERS,
  type TextTo3DRequest, 
  type TextTo3DResult 
} from '@/lib/textTo3D'
import { useAuthStore } from '@/stores/authStore'
import { useProjects } from '@/hooks/useProjects'
import { convertScadToStl } from '@/lib/scadConverter'

interface TextTo3DPageProps {
  onBack: () => void
  projectId?: string
}

const STYLE_OPTIONS = [
  { id: 'realistic', label: 'Realistic', icon: Eye },
  { id: 'cartoon', label: 'Cartoon', icon: Palette },
  { id: 'lowpoly', label: 'Low Poly', icon: Box },
  { id: 'mechanical', label: 'Mechanical', icon: Settings },
  { id: 'organic', label: 'Organic', icon: Layers },
] as const

const COMPLEXITY_OPTIONS = [
  { id: 'simple', label: 'Simple', description: 'Fast, basic shapes' },
  { id: 'medium', label: 'Medium', description: 'Balanced detail' },
  { id: 'detailed', label: 'Detailed', description: 'High detail, slower' },
] as const

const EXAMPLE_PROMPTS = [
  'A minimalist phone stand with adjustable angle',
  'Decorative vase with spiral pattern',
  'Gear mechanism with 24 teeth',
  'Wall-mounted plant holder',
  'Cable organizer with multiple slots',
  'Desk lamp with articulating arm',
]

function STLViewer({ stlUrl }: { stlUrl: string }) {
  const [geometry, setGeometry] = useStateThree<THREE.BufferGeometry | null>(null)

  useEffect(() => {
    if (!stlUrl) return
    const loader = new STLLoader()
    // Extract base64 data from data URL
    const base64Data = stlUrl.split(',')[1] || ''
    const binaryString = atob(base64Data)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    const geom = loader.parse(bytes.buffer)
    geom.center()
    geom.computeVertexNormals()
    setGeometry(geom)
  }, [stlUrl])

  if (!geometry) return null

  return (
    <mesh geometry={geometry} castShadow receiveShadow>
      <meshStandardMaterial color="#8b5cf6" metalness={0.3} roughness={0.4} />
    </mesh>
  )
}

function ModelScene({ modelUrl }: { modelUrl: string | undefined }) {
  return (
    <>
      <PerspectiveCamera makeDefault position={[50, 50, 50]} fov={50} />
      <OrbitControls enableDamping dampingFactor={0.05} />
      <ambientLight intensity={0.4} />
      <directionalLight position={[50, 50, 25]} intensity={1} castShadow />
      <directionalLight position={[-25, 25, -25]} intensity={0.3} />
      <Center>
        {modelUrl && <STLViewer stlUrl={modelUrl} />}
      </Center>
      <gridHelper args={[100, 10, '#3f3f46', '#27272a']} />
      <Environment preset="studio" />
    </>
  )
}

export default function TextTo3DPage({ onBack, projectId: initialProjectId }: TextTo3DPageProps) {
  const { user } = useAuthStore()
  const { projects } = useProjects()
  
  const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(initialProjectId)
  const [prompt, setPrompt] = useState('')
  const [negativePrompt, setNegativePrompt] = useState('')
  const [style, setStyle] = useState<TextTo3DRequest['style']>('realistic')
  const [complexity, setComplexity] = useState<TextTo3DRequest['complexity']>('medium')
  const [selectedProvider, setSelectedProvider] = useState('claude')
  const [apiKey, setApiKey] = useState('')
  
  const [isGenerating, setIsGenerating] = useState(false)
  const [result, setResult] = useState<TextTo3DResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isConverting, setIsConverting] = useState(false)
  const [convertedStlUrl, setConvertedStlUrl] = useState<string | null>(null)
  
  const [history, setHistory] = useState<TextTo3DResult[]>([])
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Convert SCAD to STL using WASM when we get a result with code
  const conversionInProgress = useRef(false)
  useEffect(() => {
    const extendedResult = result as (TextTo3DResult & { _generatedCode?: string }) | null
    const hasCode = !!extendedResult?._generatedCode
    const hasModelUrl = !!extendedResult?.modelUrl
    
    if (extendedResult?.status === 'completed' && hasCode && !hasModelUrl && !conversionInProgress.current) {
      conversionInProgress.current = true
      console.log('[TextTo3D] Starting WASM conversion...')
      setIsConverting(true)
      setConvertedStlUrl(null)
      convertScadToStl(extendedResult._generatedCode!)
        .then((conversionResult) => {
          if (conversionResult.success && conversionResult.stlUrl) {
            console.log('[TextTo3D] Conversion successful')
            setConvertedStlUrl(conversionResult.stlUrl)
          } else {
            console.error('[TextTo3D] Conversion failed:', conversionResult.error)
          }
        })
        .catch((err) => {
          console.error('[TextTo3D] Conversion exception:', err)
        })
        .finally(() => {
          setIsConverting(false)
          conversionInProgress.current = false
        })
    } else if (!extendedResult?._generatedCode) {
      setConvertedStlUrl(null)
      conversionInProgress.current = false
    }
  }, [result])

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return
    
    setIsGenerating(true)
    setError(null)
    setResult(null)
    setConvertedStlUrl(null)
    conversionInProgress.current = false
    
    try {
      if (apiKey) {
        textTo3DService.setApiKey(apiKey)
      }
      textTo3DService.setProvider(selectedProvider)
      
      const generationResult = await textTo3DService.generate({
        prompt: prompt.trim(),
        style,
        complexity,
        negativePrompt: negativePrompt.trim() || undefined,
        outputFormat: 'glb',
      })
      
      setResult(generationResult)
      setHistory(prev => [generationResult, ...prev.slice(0, 9)])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed')
    } finally {
      setIsGenerating(false)
    }
  }, [prompt, negativePrompt, style, complexity, selectedProvider, apiKey])

  const handleSave = async () => {
    if (!result || !selectedProjectId || !user) return
    
    setIsSaving(true)
    try {
      await textTo3DService.saveToProject(result, selectedProjectId, user.id)
      onBack()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  const handleExampleClick = (example: string) => {
    setPrompt(example)
    textareaRef.current?.focus()
  }

  const handleRegenerate = () => {
    handleGenerate()
  }

  const currentProvider = TEXT_TO_3D_PROVIDERS.find(p => p.id === selectedProvider)

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-surface-800 text-surface-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-ai-400" />
                Text to 3D
              </h1>
              <p className="text-surface-400 text-sm">Generate 3D models from text descriptions using AI</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings className="w-4 h-4" />
            Settings
            <ChevronDown className={cn('w-4 h-4 transition-transform', showSettings && 'rotate-180')} />
          </Button>
        </div>

        {/* Settings Panel */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <Card className="p-5">
                <h3 className="font-medium text-white mb-4">AI Provider Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-surface-400 block mb-2">Provider</label>
                    <select
                      value={selectedProvider}
                      onChange={(e) => setSelectedProvider(e.target.value)}
                      className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-ai-500"
                    >
                      {TEXT_TO_3D_PROVIDERS.map(provider => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-surface-500 mt-1">{currentProvider?.description}</p>
                  </div>
                  
                  {currentProvider?.requiresApiKey && (
                    <div>
                      <label className="text-sm text-surface-400 block mb-2">API Key</label>
                      <input
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder="Enter your API key..."
                        className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white text-sm placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-ai-500"
                      />
                      <p className="text-xs text-surface-500 mt-1">
                        Your API key is stored locally and never sent to our servers
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Input */}
          <div className="space-y-4">
            {/* Prompt Input */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="w-5 h-5 text-ai-400" />
                <h3 className="font-medium text-white">Describe Your Model</h3>
              </div>
              
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the 3D model you want to create... Be specific about shape, size, features, and purpose."
                rows={4}
                className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-xl text-white placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-ai-500 resize-none"
              />
              
              <div className="mt-3">
                <p className="text-xs text-surface-500 mb-2">Try an example:</p>
                <div className="flex flex-wrap gap-2">
                  {EXAMPLE_PROMPTS.slice(0, 3).map((example) => (
                    <button
                      key={example}
                      onClick={() => handleExampleClick(example)}
                      className="px-3 py-1.5 bg-surface-800 hover:bg-surface-700 rounded-full text-xs text-surface-300 transition-colors truncate max-w-[200px]"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            {/* Style Selection */}
            <Card className="p-5">
              <h3 className="font-medium text-white mb-3">Style</h3>
              <div className="grid grid-cols-5 gap-2">
                {STYLE_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setStyle(option.id)}
                    className={cn(
                      'flex flex-col items-center gap-2 p-3 rounded-xl transition-all',
                      style === option.id
                        ? 'bg-ai-500/20 border-2 border-ai-500 text-ai-400'
                        : 'bg-surface-800 border-2 border-transparent hover:border-surface-600 text-surface-400'
                    )}
                  >
                    <option.icon className="w-5 h-5" />
                    <span className="text-xs">{option.label}</span>
                  </button>
                ))}
              </div>
            </Card>

            {/* Complexity Selection */}
            <Card className="p-5">
              <h3 className="font-medium text-white mb-3">Complexity</h3>
              <div className="grid grid-cols-3 gap-3">
                {COMPLEXITY_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setComplexity(option.id)}
                    className={cn(
                      'flex flex-col items-start p-3 rounded-xl transition-all text-left',
                      complexity === option.id
                        ? 'bg-ai-500/20 border-2 border-ai-500'
                        : 'bg-surface-800 border-2 border-transparent hover:border-surface-600'
                    )}
                  >
                    <span className={cn(
                      'font-medium text-sm',
                      complexity === option.id ? 'text-ai-400' : 'text-white'
                    )}>
                      {option.label}
                    </span>
                    <span className="text-xs text-surface-500">{option.description}</span>
                  </button>
                ))}
              </div>
            </Card>

            {/* Advanced Options */}
            <Card className="p-5">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer">
                  <h3 className="font-medium text-white">Advanced Options</h3>
                  <ChevronDown className="w-4 h-4 text-surface-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="text-sm text-surface-400 block mb-2">Negative Prompt</label>
                    <input
                      type="text"
                      value={negativePrompt}
                      onChange={(e) => setNegativePrompt(e.target.value)}
                      placeholder="What to avoid (e.g., low quality, distorted)"
                      className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white text-sm placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-ai-500"
                    />
                  </div>
                </div>
              </details>
            </Card>

            {/* Generate Button */}
            <Button
              variant="ai"
              size="lg"
              className="w-full"
              onClick={handleGenerate}
              disabled={!prompt.trim() || isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" />
                  Generate 3D Model
                </>
              )}
            </Button>

            {/* Error Display */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
              >
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-400">Generation Failed</p>
                    <p className="text-sm text-red-300/80">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-4">
            {/* 3D Preview */}
            <Card className="overflow-hidden">
              <div className="aspect-square bg-surface-950 relative">
                {isGenerating ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="relative">
                      <div className="w-20 h-20 border-4 border-ai-500/30 rounded-full" />
                      <div className="absolute inset-0 w-20 h-20 border-4 border-ai-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="mt-4 text-sm text-surface-400">Generating your model...</p>
                    <p className="text-xs text-surface-500">This may take 10-30 seconds</p>
                  </div>
                ) : isConverting ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="relative">
                      <div className="w-16 h-16 border-4 border-purple-500/30 rounded-full" />
                      <div className="absolute inset-0 w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="mt-4 text-sm text-surface-400">Converting SCAD to 3D mesh...</p>
                    <p className="text-xs text-surface-500">Using OpenSCAD WASM</p>
                  </div>
                ) : result && result.status === 'completed' ? (
                  (result.modelUrl || convertedStlUrl) ? (
                    <Canvas shadows className="absolute inset-0">
                      <ModelScene modelUrl={result.modelUrl || convertedStlUrl || undefined} />
                    </Canvas>
                  ) : (
                    <div className="absolute inset-0 flex flex-col">
                      <div className="bg-surface-800 px-4 py-2 border-b border-surface-700 flex items-center justify-between">
                        <span className="text-sm text-surface-300 font-mono">Generated OpenSCAD Code</span>
                        <button
                          onClick={() => {
                            const code = (result as any)._generatedCode || ''
                            const blob = new Blob([code], { type: 'text/plain' })
                            const url = URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = `model-${result.id.slice(0, 8)}.scad`
                            a.click()
                            URL.revokeObjectURL(url)
                          }}
                          className="px-3 py-1 bg-ai-500 hover:bg-ai-600 text-white text-xs rounded-lg transition-colors"
                        >
                          Download .scad
                        </button>
                      </div>
                      <pre className="flex-1 overflow-auto p-4 text-xs text-green-400 font-mono bg-surface-950">
                        {(result as any)._generatedCode || 'No code generated'}
                      </pre>
                    </div>
                  )
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Box className="w-16 h-16 text-surface-600 mb-4" />
                    <p className="text-surface-400">Your generated model will appear here</p>
                    <p className="text-xs text-surface-500 mt-1">Enter a prompt and click Generate</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Result Info */}
            {result && result.status === 'completed' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <Card className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-white">Generated Model</h3>
                    <span className="px-2 py-1 bg-success-500/20 text-success-400 text-xs rounded-full">
                      Complete
                    </span>
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-surface-400">Prompt</span>
                      <span className="text-surface-200 text-right max-w-[200px] truncate">
                        {result.prompt}
                      </span>
                    </div>
                    {result.metadata && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Triangles</span>
                          <span className="text-surface-200">
                            {result.metadata.triangleCount.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Dimensions</span>
                          <span className="text-surface-200">
                            {result.metadata.boundingBox.width} × {result.metadata.boundingBox.height} × {result.metadata.boundingBox.depth}mm
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-surface-400">Generation Time</span>
                          <span className="text-surface-200">
                            {result.metadata.generationTime.toFixed(1)}s
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </Card>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <Button variant="secondary" onClick={handleRegenerate}>
                    <RefreshCw className="w-4 h-4" />
                    Regenerate
                  </Button>
                  <Button variant="secondary">
                    <Copy className="w-4 h-4" />
                    Duplicate
                  </Button>
                </div>
                
                {/* Project Selector */}
                <div className="space-y-2">
                  <label className="text-sm text-surface-400">Save to Project</label>
                  <select
                    value={selectedProjectId || ''}
                    onChange={(e) => setSelectedProjectId(e.target.value || undefined)}
                    className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-ai-500"
                  >
                    <option value="">Select a project...</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                  {projects.length === 0 && (
                    <p className="text-xs text-surface-500">
                      Create a project first to save models
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button variant="secondary">
                    <Download className="w-4 h-4" />
                    Export GLB
                  </Button>
                  <Button 
                    onClick={handleSave}
                    disabled={isSaving || !selectedProjectId}
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save to Project
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Generation History */}
            {history.length > 0 && (
              <Card className="p-5">
                <h3 className="font-medium text-white mb-3">Recent Generations</h3>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {history.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setResult(item)}
                      className={cn(
                        'w-full flex items-center gap-3 p-2 rounded-lg transition-colors text-left',
                        result?.id === item.id
                          ? 'bg-ai-500/20 border border-ai-500/30'
                          : 'hover:bg-surface-800'
                      )}
                    >
                      <div className="w-10 h-10 bg-surface-800 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Box className="w-5 h-5 text-ai-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{item.prompt}</p>
                        <p className="text-xs text-surface-500">
                          {item.createdAt.toLocaleTimeString()}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
